// Mirror of cobstream-backend/src/services/sportsStreaming/sports.types.ts
// The shared shape is small enough that a hand-mirror is fine — same pattern
// as types/streaming.ts. Drift surfaces as runtime narrowing failures in
// the slice + component code (and the tests).

export const SPORTS_SOURCE_NAMES = ['streamed', 'ppv', 'sportsrc', 'daddylive', 'bintvdrm', 'roxie'] as const;
export type SportsProviderName = (typeof SPORTS_SOURCE_NAMES)[number];

/**
 * Providers that serve their stream as MPEG-DASH + ClearKey instead of HLS.
 * These take the /dash-init + Shaka path; everything else uses /playlist.m3u8 +
 * Hls.js. Keep in sync with the backend's `manifestType === 'dash'` providers.
 */
export const DASH_SPORTS_PROVIDERS: ReadonlySet<SportsProviderName> = new Set(['bintvdrm']);

export function isDashSportsProvider(provider: SportsProviderName | null | undefined): boolean {
  return provider != null && DASH_SPORTS_PROVIDERS.has(provider);
}

export function isKnownSportsProvider(value: unknown): value is SportsProviderName {
  return typeof value === 'string' && (SPORTS_SOURCE_NAMES as readonly string[]).includes(value);
}

export const CANONICAL_SPORTS = [
  'football',
  'basketball',
  'ice-hockey',
  'american-football',
  'baseball',
  'afl',
  'cricket',
  'rugby',
  'motor-sports',
  'fight',
  'tennis',
  'golf',
  'darts',
  'billiards',
  'wrestling',
  '247',
  'other',
] as const;
export type CanonicalSportId = (typeof CANONICAL_SPORTS)[number];

export interface SportInfo {
  id: CanonicalSportId;
  name: string;
}

export interface SportsTeam {
  name: string;
  badge?: string;
}

export interface SportsEventSource {
  provider: SportsProviderName;
  externalId: string;
  embedUrl: string;
  streamNo?: number;
  quality?: string;
  /** Human language of this feed (e.g. 'English', 'Spanish'), when tagged. */
  language?: string;
  substreamOf?: string;
}

/**
 * One user-selectable playback variant within an event — the unit the source
 * menu's per-provider sub-dropdown renders. Mirrors the backend
 * SportsStreamVariant; served by GET /api/sports-stream/:eventId/sources.
 */
export interface SportsStreamVariant {
  provider: SportsProviderName;
  /** source.externalId — disambiguates variants within a provider (?sourceId=). */
  sourceId: string;
  /** streamed: the 1..N per-source stream index. */
  streamNo?: number;
  language?: string;
  quality?: string;
  hd?: boolean;
  /** Pre-built display label from the backend. */
  label: string;
}

export interface SportsEventVariants {
  eventId: string;
  providers: Array<{ provider: SportsProviderName; variants: SportsStreamVariant[] }>;
}

export interface SportsEvent {
  id: string;
  title: string;
  sport: CanonicalSportId;
  league?: string;
  /** Unix ms — 0 for always-live (24/7) entries. */
  startsAt: number;
  /** Unix ms — null for always-live (24/7) entries. */
  endsAt: number | null;
  posterUrl: string | null;
  teams?: { home: SportsTeam; away: SportsTeam };
  popular: boolean;
  sources: SportsEventSource[];
}

export type SportsPreflightKind =
  | 'not_supported'
  | 'upstream_4xx'
  | 'upstream_5xx'
  | 'parse_failed'
  | 'decrypt_failed'
  | 'timeout'
  | 'unknown'
  | 'playlist_http'
  | 'variant_http'
  | 'segment_http';

export interface SportsPreflightResult {
  ok: boolean;
  providerName: SportsProviderName;
  kind?: SportsPreflightKind;
  message?: string;
}

/** Live = startsAt <= now < (endsAt ?? Infinity). 247 entries are always live. */
export function isEventLive(event: SportsEvent, now: number = Date.now()): boolean {
  if (event.sport === '247') return true;
  if (event.startsAt > now) return false;
  if (event.endsAt === null) return true;
  return now < event.endsAt;
}

/**
 * Grace window after `endsAt` before the event is dropped from the page.
 * Mirrors the backend's `SPORTS_ENDED_HIDE_GRACE_MS` (10 min). Belt-and-
 * suspenders: the backend's 60s catalog refresh + the frontend's 60s repoll
 * mean an event can otherwise linger for up to two minutes past its true
 * end before the API stops serving it.
 */
export const SPORTS_ENDED_HIDE_GRACE_MS = 10 * 60_000;

/** Event has finished and its grace window has elapsed. */
export function isEventEnded(event: SportsEvent, now: number = Date.now()): boolean {
  if (event.endsAt === null) return false;
  return event.endsAt + SPORTS_ENDED_HIDE_GRACE_MS <= now;
}
